import { Component, ElementRef, HostListener, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Event } from '../../../../model/event';
import { AdminService } from '../../../../service/admin.service';
import { EventsService } from '../../../../service/events.service';
import { EventTopic, SHOW_ALL_EVENT_TOPIC_OPTIONS } from '../../../../model/eventTopic';
import { FormGroup, FormBuilder, Validators, AbstractControl, FormControl ,ValidatorFn } from '@angular/forms';
import { SelectOption } from 'src/app/model/select.option';
import { NotificationService } from 'src/app/service/notification.service';
import { SHOW_ALL_PAGINATION_OPTIONS } from '../../../../model/pagination.options';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { EVENT_ADMIN_ICONS } from '../../event-admin-icons';
import { HasUnsavedChanges } from 'src/app/guard/unsaved-changes.guard';

@Component({
  selector: 'app-event-topics',
  templateUrl: './event-topics.component.html',
  styleUrls: ['./event-topics.component.scss']
})
export class EventTopicsComponent implements OnInit, HasUnsavedChanges {
  event: Event;
  topicOptions: Array<SelectOption>;

  inheritTopicsFromEvent: Array<EventTopic>;
  deletedTopics: Array<EventTopic> = [];
  selectedEventName: string;

  topic: string;
  addTopic: string;
  newTopicControl: AbstractControl;
  topicForm: FormGroup;
  eventForm: FormGroup;

  saved = true;
  saving = false;
  // Sinaliza que `loadData()` já completou e a lista de tópicos chegou.
  // O template usa pra atrasar a render do `<mat-card>` até ter conteúdo —
  // evita o flash de card vazio enquanto o backend responde.
  topicsLoaded = false;

  // Mapa central de ícones (template referencia `icons.topics` no header).
  readonly icons = EVENT_ADMIN_ICONS;

  // ─── Inline add (footer-add pattern) ─────────────────────────────────
  // Substitui a seção "Adicionar novos tópicos" autônoma por inputs dentro
  // da própria lista: footer-add no rodapé (1º nível) e por-parent (2º nível).
  addingRoot = false;
  rootNewTopic = '';
  addingSubFor: number | null = null;  // índice do parent com sub-add ativo
  subNewTopic = '';

  // Diálogo modal de "Importar tópicos de outro evento".
  @ViewChild('importTopicsDialog', { static: true }) importTopicsDialogTpl!: TemplateRef<unknown>;
  private importDialogRef: MatDialogRef<unknown> | null = null;

  // Lista filtrável de eventos disponíveis pra importar (substitui o dropdown).
  availableEvents: Array<{ id: number; label: string }> = [];
  eventFilter = '';
  selectedImportEventId: number | null = null;
  loadingEvents = false;
  loadingTopics = false;

  openImportDialog(): void {
    this.loadAvailableEvents();
    this.importDialogRef = this.dialog.open(this.importTopicsDialogTpl, {
      width: '560px',
      maxWidth: '92vw',
      autoFocus: false,
      panelClass: 'import-topics-dialog-panel',
    });
    this.importDialogRef.afterClosed().subscribe(() => {
      // Reset do estado pra não vazar pra próxima abertura.
      this.inheritTopicsFromEvent = null;
      this.selectedEventName = null;
      this.selectedImportEventId = null;
      this.eventFilter = '';
      this.importDialogRef = null;
    });
  }

  private loadAvailableEvents(): void {
    // Carrega lista compacta de todos os eventos e exclui o atual.
    this.loadingEvents = true;
    this.eventsService.getAllEventsWithMinimumDetails().subscribe(events => {
      this.availableEvents = events
        .filter(e => e.id !== this.event?.id)
        .map(e => ({ id: e.id, label: `${e.acronym} ${e.year} - ${e.name}` }))
        .sort((a, b) => a.label.localeCompare(b.label));
      this.loadingEvents = false;
    }, () => { this.loadingEvents = false; });
  }

  filteredAvailableEvents(): Array<{ id: number; label: string }> {
    const q = this.eventFilter.trim().toLocaleLowerCase();
    if (!q) { return this.availableEvents; }
    return this.availableEvents.filter(e => e.label.toLocaleLowerCase().includes(q));
  }

  selectImportEvent(eventId: number): void {
    this.selectedImportEventId = eventId;
    this.pickEvent(eventId);
  }

  confirmImport(): void {
    if (this.inheritTopicsFromEvent?.some(e => e.active || e.children?.some(c => c.active))) {
      this.pushInheritedTopics(this.inheritTopicsFromEvent);
    }
    this.importDialogRef?.close();
  }

  // Parser preview: splita o texto por `;` e retorna a lista de nomes
  // confirmando o que vai virar tópico. Usado pra feedback inline ao digitar.
  parseTopicPreview(raw: string): string[] {
    if (!raw) { return []; }
    return raw.split(';').map(t => t.trim()).filter(t => t.length);
  }

  // Disparo do sort por nome a partir do botão no header (usa a lista raiz).
  sortRootByName(): void {
    if (this.topicForm?.value?.topics?.length) {
      this.sortByName(this.topicForm.value.topics);
    }
  }


  @ViewChildren('inlineAddInput') inlineAddInputs!: QueryList<ElementRef<HTMLInputElement>>;

  startAddingRoot(): void {
    this.addingRoot = true;
    setTimeout(() => this.inlineAddInputs.first?.nativeElement?.focus(), 0);
  }

  stopAddingRoot(): void {
    if (!this.rootNewTopic) { this.addingRoot = false; }
  }

  submitRootAdd(): void {
    const name = this.rootNewTopic.trim();
    if (!name) { return; }
    this.topicForm.patchValue({ newTopic: name });
    this.pushNewTopic(name, null);
    this.rootNewTopic = '';
    // Permanece em modo de adição pra permitir múltiplos adds em sequência;
    // Esc/blur sem texto fecha.
    setTimeout(() => this.inlineAddInputs.first?.nativeElement?.focus(), 0);
  }

  startAddingSub(idx: number): void {
    this.addingSubFor = idx;
    this.subNewTopic = '';
    setTimeout(() => this.inlineAddInputs.last?.nativeElement?.focus(), 0);
  }

  stopAddingSub(): void {
    if (!this.subNewTopic) { this.addingSubFor = null; }
  }

  submitSubAdd(parent: EventTopic): void {
    const name = this.subNewTopic.trim();
    if (!name) { return; }
    this.topicForm.patchValue({ newTopic: name });
    this.pushNewTopic(name, parent);
    this.subNewTopic = '';
    setTimeout(() => this.inlineAddInputs.last?.nativeElement?.focus(), 0);
  }

  /** Implementação de HasUnsavedChanges: o guard usa isso pra decidir se
   * intercepta a navegação com diálogo de confirmação. Estado "dirty" é o
   * mesmo flag `saved` que rege a floating-submit-bar. */
  hasUnsavedChanges(): boolean {
    return !this.saved;
  }

  /** Discard: recarrega tópicos do servidor (origem da verdade). Reverte
   * adições/remoções/reordenamentos locais pendentes. min/max do eventForm
   * são restaurados a partir do this.event original. */
  discardChanges(): void {
    this.deletedTopics = [];
    this.loadData();
    if (this.eventForm && this.event) {
      this.eventForm.patchValue({
        minTopics: this.event.minTopics,
        maxTopics: this.event.maxTopics,
      });
    }
    this.saved = true;
  }

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private adminService: AdminService,
    private eventsService: EventsService,
    private notificationService: NotificationService
  ) { }

  ngOnInit() {
    setTimeout(() => {
      this.event = this.adminService.selectedEvent;
      if (!this.event) {
        this.adminService.getEvent().subscribe(event => {
          this.event = event;
          this.initForm();
          this.initEventForm();
          this.loadData();
        });
      } else {
        this.initForm();
        this.initEventForm();
        this.loadData();
      }
    });
  }

  initForm() {
    this.topicForm = this.fb.group({
      topics: [[], Validators.required],
      eventParent: [],
      newTopic: ['', [Validators.minLength(2)]],
      topicParent: [undefined],
      sortBy: ['ascending']
    });

    this.newTopicControl = this.topicForm.get('newTopic');
    this.topicForm.get('topics').valueChanges.subscribe( v => {
      this.saved = false;
    });
  }

  loadData() {
    this.eventsService.getEventTopics(this.event.id, SHOW_ALL_EVENT_TOPIC_OPTIONS).subscribe(topics => {
      this.topicForm.patchValue({topics});
      this.updateTopicOptions(this.topicForm.value.topics);
      this.saved = true;
      this.topicsLoaded = true;
    });
  }

  pickEvent(eventId: number) {
    this.loadingTopics = true;
    this.inheritTopicsFromEvent = null;
    this.eventsService.getEventTopics(eventId).subscribe(topics => {
      topics.forEach(topic => topic.children.map(child => child.parent = topic));
      this.inheritTopicsFromEvent = topics;
      this.eventsService.getEvent(eventId).subscribe(event => this.selectedEventName = event.name);
      this.loadingTopics = false;
    }, () => { this.loadingTopics = false; });
  }

  updateTopicOptions(topics: EventTopic[]) {
    this.saved = false;
    const oldParentTopic = this.topicForm.value.topicParent;
    this.topicOptions = [{ id: undefined, value: 'None'}];
    this.topicOptions.push(...topics.map( topic => ({ id: topic, value: topic.name})));

    if (oldParentTopic) {
      const findFunction = ( y => oldParentTopic.id ? ((x) => x.id === y.id) : ((x) => x.name === y.name));
      this.topicForm.patchValue({ topicParent: topics.find(findFunction(oldParentTopic)) });
    }
  }

  pushInheritedTopics(topics) {
    const currentTopics = this.topicForm.value.topics;
    currentTopics.push(...topics.filter(e => e.active).map(e => {
      const parentTopic = new EventTopic(e.name);
      parentTopic.children = e.children.filter(sub => sub.active).map(sub => new EventTopic(sub.name));

      return parentTopic;
    }));
    this.topicForm.patchValue({ topics: currentTopics });
    this.updateTopicOptions(this.topicForm.value.topics);
    this.notificationService.notify('admin.event.topics.added-topics');
    this.markAllInheritedTopics(false, topics);
  }

  markAllInheritedTopics(mark: boolean, topics, onlyValids = false) {
    this.saved = false;
    topics.forEach(item => {
      if (!onlyValids) {
        item.active = mark;
      } else {
        if (item.children.length > 0) {
          item.active = mark;
        }
      }

      if (item.children) {
        item.children.forEach(sub => sub.active = mark);
      }
    });
  }

  markParent(topic: EventTopic, parent: EventTopic) {
    this.saved = false;
    if (topic.active && !parent.active) {
      parent.active = true;
    } else if (!parent.children.some(x => x.active)) {
      parent.active = false;
    }
  }

  // Visibilidade é INDEPENDENTE entre os níveis: toggle raiz não afeta
  // children, toggle de child não afeta o parent. Cada tópico tem sua
  // própria flag `active`.
  toggleVisibility(item: EventTopic, _parent?: EventTopic) {
    item.active = !item.active;
  }

  pushNewTopic(topic_name: string, parent: EventTopic = null) {
    if (this.newTopicControl.valid && this.newTopicControl.value) {
      const newTopics = topic_name.split(';').map(t => t.trim()).filter(t => t.length).map(t => new EventTopic(t));

      newTopics.forEach(topic => {
        topic.active = true;
        if (parent) {
          parent.children.push(topic);
          this.markParent(topic, parent);
        } else {
          const currentTopics = this.topicForm.value.topics;
          currentTopics.push(topic);

          this.topicForm.patchValue({ topics: currentTopics });
          this.updateTopicOptions(this.topicForm.value.topics);
        }
      });

      this.saved = false;
      this.topicForm.patchValue({ newTopic: '' });
    }
  }

  removeTopic(topic: EventTopic, parent: EventTopic = null) {
    if ( parent ) {
      this.removeTopicAndUpdate(topic, parent.children);
    } else {
      const arrayToDelete = this.topicForm.value.topics;
      if (!topic.children.length) {
        this.removeTopicAndUpdate(topic, arrayToDelete);
      } else {
        this.dialog.open(ConfirmationDialogComponent, {
          data: {
            title: 'admin.event.topics.confirm-delete-parent-topic-dialog',
            content: 'admin.event.topics.warning-delete-subtopics'
          }
        }).afterClosed().subscribe(confirmed => {
          if (confirmed) {
            this.removeTopicAndUpdate(topic, arrayToDelete);
        }});
      }
    }
  }

  removeTopicAndUpdate(topic, arrayToDelete) {
    const index = arrayToDelete.findIndex(obj => obj === topic);
    const obj_deleted = arrayToDelete.splice( index, 1)[0];
    this.deletedTopics.push(obj_deleted);
    this.updateTopicOptions(this.topicForm.value.topics);
  }

  submit() {
    this.submitSettingTopics();
    const formTopics: EventTopic[] = this.topicForm.value.topics.slice();
    // fixing the property order in topics and subtopics to save to back
    formTopics.forEach((topic, index) => {
      topic.order = index;
      topic.children.forEach((sub, subIndex) => sub.order = subIndex);
    });

    formTopics.push(...this.deletedTopics
      .filter(topic => topic.id)
      .map( topic => {
        topic.deleted = true;
        return topic;
      }));
    this.deletedTopics = [];

    this.adminService.progressBar.start();
    this.saving = true;
    // Notificação verde de "salvando" removida: o feedback de progresso já
    // vem da `progressBar` e do estado `saving` na floating-submit-bar.

    this.eventsService.putEventTopics(this.event.id, formTopics).subscribe(topics => {
      this.event.topics = topics;
      this.topicForm.patchValue({topics: topics});
      // Notificação verde de "salvo com sucesso" removida: o desaparecimento
      // da floating-submit-bar (via `saved = true` abaixo) é o feedback de
      // sucesso suficiente.
      this.updateTopicOptions(this.topicForm.value.topics);
      this.adminService.progressBar.stop();
      this.saving = false;
      this.saved = true;
    }, () => {
      this.adminService.progressBar.stop();
      this.saving = false;
    });
  }

  sortBy(list, sortFunction: (a, b) => number) {
    list.sort(sortFunction);
    list.forEach(item => item.children.sort(sortFunction));
  }

  sortByCategory(a, b): number {
    if (a.isCategory > b.isCategory) {
      return 1;
    } else if (a.isCategory < b.isCategory) {
      return -1;
    } else {
      return 0;
    }
  }

  sortByName(list) {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.topics.warning-sort-topics',
        content: ''
      }
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        const sortByControl = this.topicForm.get('sortBy');
        this.saved = false;

        if (sortByControl.value === 'ascending') {
          this.sortBy(list, ((a, b) => {
            const category = this.sortByCategory(a, b);
            return category !== 0 ? category : a.name.localeCompare(b.name);
          }));
          sortByControl.setValue('descending');
        } else {
          this.sortBy(list, ((a, b) => {
            const category = this.sortByCategory(a, b);
            return category !== 0 ? category : b.name.localeCompare(a.name);
          }));
          sortByControl.setValue('ascending');
        }
    }});
  }

  // ─── Drag-and-drop com cancelamento via ESC ────────────────────────
  // Padrão: usuário arrasta → solta. ESC durante drag = restaurar ordem
  // original (não aplica `moveItemInArray`). A liberação visual do drag é
  // disparada via `pointerup` sintético que faz o CDK chamar `drop()`;
  // o flag `escapeRequested` impede a mutação do array nesse caso.
  private dragInProgress = false;
  private escapeRequested = false;

  onDragStarted(): void {
    this.dragInProgress = true;
    this.escapeRequested = false;
  }

  onDragEnded(): void {
    this.dragInProgress = false;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (!this.dragInProgress) { return; }
    this.escapeRequested = true;
    // Libera o pointer pra encerrar o drag; CDK chama drop() em sequência.
    document.dispatchEvent(new PointerEvent('pointerup', { bubbles: true, cancelable: true }));
    document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
  }

  drop(list, event) {
    if (this.escapeRequested) {
      this.escapeRequested = false;
      return;
    }
    this.saved = false;
    moveItemInArray(list, event.previousIndex, event.currentIndex);
  }

  private initEventForm(): void {
    this.eventForm = this.fb.group({
      name: [this.event.name],
      acronym: [this.event.acronym],
      uri: [this.event.uri],
      year: [this.event.year],
      eventType: [this.event.eventType.id],
      eventParent: [this.event.eventParent instanceof Event ? this.event.eventParent.id : this.event.eventParent],
      minTopics: [this.event.minTopics],
      maxTopics: [this.event.maxTopics]
    }, { updateOn: 'blur' });
    
    this.listenToTopicNumberChanges();
    (<FormControl>this.eventForm.get('minTopics')).setValidators(this.topicRangeValidator);
    (<FormControl>this.eventForm.get('maxTopics')).setValidators(this.topicRangeValidator);
  }

  topicRangeValidator: ValidatorFn = (fg: FormGroup) => {
    const start = this.eventForm.get('minTopics').value;
    const end = this.eventForm.get('maxTopics').value;

    return start !== null && end !== null && ((start <= end) || (start > 0 && end == 0))
      ? null
      : { range: true };
  }

  private listenToTopicNumberChanges(): void{
    this.eventForm.get('maxTopics').valueChanges.subscribe(() => {
      this.saved = false;
    });
    this.eventForm.get('minTopics').valueChanges.subscribe(() => {
      this.saved = false;
    });
  }

  submitSettingTopics() {
    if (!this.eventForm.valid) {
      // To catch unvalidated min/max topics, confirm validity of topic controls.
      this.eventForm.get('minTopics').updateValueAndValidity();
      this.eventForm.get('maxTopics').updateValueAndValidity();
    }

    if (this.eventForm.valid) {
      const event = new Event(this.eventForm.value.name, this.eventForm.value.acronym);
      event.uri = this.eventForm.value.uri;
      event.year = this.eventForm.value.year;
      event.eventType = this.eventForm.value.eventType;
      event.eventParent = this.eventForm.value.eventParent ? this.eventForm.value.eventParent : null;

      event.minTopics = this.eventForm.value.minTopics;
      event.maxTopics = this.eventForm.value.maxTopics;

      this.eventsService.editEvent(this.event.id, event).subscribe(e => {
        this.adminService.selectedEvent = e;
        this.event = e;
        // Notificação verde removida: o desaparecimento da floating-submit-bar
        // (via `saved = true` no fluxo do submit) já sinaliza sucesso.
      });
    } else {
      this.eventForm.markAllAsTouched();
    }
  }
}
